import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { AgencyPermission, AgencyRoleCode, ROLE_PERMISSIONS } from '../shared/utils/agency-rbac';

/**
 * Front-side view of the connected agency account's permissions (UI gating only — every rule is
 * enforced by the backend).
 */
@Injectable({ providedIn: 'root' })
export class PermissionService {
  constructor(private auth: AuthService) {}

  permissions(): string[] {
    const user: any = this.auth.user;
    if (!user) {
      return [];
    }
    if (Array.isArray(user.permissions)) {
      return user.permissions;
    }
    // Session stored before the RBAC release: apply the same defaults as the backend
    // (legacy AGENCY account = admin, legacy AGENCY_USER = recruteur).
    if (user.kind === 'AGENCY') {
      return ROLE_PERMISSIONS.ADMIN;
    }
    if (user.kind === 'AGENCY_USER') {
      return ROLE_PERMISSIONS[(user.role as AgencyRoleCode) ?? 'RECRUTEUR'] ?? ROLE_PERMISSIONS.RECRUTEUR;
    }
    return [];
  }

  /**
   * Un droit, ou un tableau de droits (au moins un suffit). Sans droit demandé : toujours vrai.
   * Le tableau sert aux écrans ouverts par plusieurs droits (ex. « Gestion des collaborateurs »).
   */
  has(permission: AgencyPermission | string | (AgencyPermission | string)[] | null | undefined): boolean {
    if (!permission || (Array.isArray(permission) && permission.length === 0)) {
      return true;
    }
    if (Array.isArray(permission)) {
      return this.hasAny(permission);
    }
    return this.permissions().includes(permission);
  }

  hasAny(permissions: (AgencyPermission | string)[]): boolean {
    if (!permissions || permissions.length === 0) {
      return true;
    }
    const granted = this.permissions();
    return permissions.some((p) => granted.includes(p));
  }

  /** Administrateur du service (rôle ADMIN, ou compte AGENCY historique) — seul à pouvoir toucher un compte admin. */
  get isAdmin(): boolean {
    const user: any = this.auth.user;
    return !!user && (user.role === 'ADMIN' || (user.role == null && user.kind === 'AGENCY'));
  }

  get isMainAccount(): boolean {
    const user: any = this.auth.user;
    return !!user && (user.main_account === true || (user.main_account == null && user.kind === 'AGENCY'));
  }
}
